(ns arthur.events.edit
  "The one way an event changes the loaded document.

  Three things have to happen together and the bug is any one of them being
  forgotten: the clip in `footage/store` is edited, the id app-db refers to it by
  is updated — `edit-clip!` may INSTALL A COPY, because a built-in clip is a
  delayed value that must stay reusable — and `:paint/revision` is bumped so the
  layer-3 subs downstream of `::render/clip` recompute. The revision exists
  because the clip itself is behind a handle: app-db holds an id, the id does not
  change when the document does, and a sub keyed only on the id would never see
  the edit.

  It started life private inside `events/paint`, which was right while polygons
  were the only thing anyone could edit. They are not.

  It is also where UNDO is recorded, for the same reason: being the one way a
  person changes the document, it is the one place that sees every change they
  make — and nothing else. A collaborator's write and an undo itself go through
  `replace-entry`, which is this without the recording."
  (:require [arthur.domain.history :as history]
            [arthur.domain.leaf :as leaf]
            [arthur.footage.store :as store]))

(defn leaves
  "The clip as leaves, which is what a history step is made of; nil for a clip
  that has no leaf form."
  [clip]
  (try (leaf/leaves "u" clip) (catch :default _ nil)))

(defn- recorded [f]
  (fn [entry]
    (let [after (f entry)
          b     (when-not (identical? (:clip entry) (:clip after)) (leaves (:clip entry)))
          a     (when b (leaves (:clip after)))]
      (cond-> after
        a (assoc :history (history/record (:history entry) b a (js/Date.now)))))))

(defn replace-entry
  "Apply `f` to the loaded entry without recording it as a step of yours."
  [db f]
  (let [id (store/edit-entry! (:clip/current db) f)]
    (if id
      (-> db
          (assoc :clip/current id)
          (update :paint/revision (fnil inc 0))
          (update :project merge {:status "edited · unsaved"}))
      db)))

(defn read-only?
  "Somebody else's project that we may look at and not write."
  [db]
  (false? (get-in db [:project :can-edit?])))

(defn edit-entry
  "Apply `f` to the loaded ENTRY — the document and the blocks, footage and
  source tracks beside it — and return the new db. For an edit that brings tier-2
  data in with it, which a document edit alone cannot.

  NOTHING, IN A PROJECT YOU MAY ONLY LOOK AT. It used to take the edit and then
  never save it, so a drawing stayed until the next reload and was gone after.
  Here, as the one way a person changes the document, it is one rule rather
  than one per command."
  [db f]
  (if (read-only? db) db (replace-entry db (recorded f))))

(defn transport
  "App-db's copy of what the transport reads off the clip, after the clip was
  replaced under it — as `::events.project/project-setting` writes it."
  [db clip]
  (cond-> (update db :clip merge (select-keys clip [:width :height]))
    (not= (:fps clip) (get-in db [:clip :fps]))
    (update :clip merge {:fps (:fps clip)})))

(defn history
  "Apply `f` to the loaded entry's undo history, which is not an edit: nothing
  is redrawn and nothing becomes unsaved."
  [db f]
  (if-let [id (store/edit-entry! (:clip/current db) #(update % :history f))]
    (assoc db :clip/current id)
    db))

(defn edit
  "Apply `f` to the loaded clip and return the new db."
  [db f]
  (edit-entry db #(update % :clip f)))

(defn transaction
  "One command is one undo step, independent of neighboring edits or timing."
  [db f]
  (-> db
      (history history/hold)
      (edit f)
      (history history/settle)))
